import React, { useState, useEffect, useCallback, useRef } from "react";
import API from "../../api/api";
import { triggerBrowserFileDownload, triggerJobAutoDownload } from "../../utils/pdfDownloadHelper";
import Swal from "sweetalert2";
import {
  FaDownload,
  FaFilePdf,
  FaSpinner,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimes,
  FaClock,
  FaLayerGroup,
  FaHdd,
  FaTrashAlt,
  FaSync,
  FaStop,
  FaBan,
  FaRedo,
} from "react-icons/fa";

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const diffSec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const mins = Math.floor(diffSec / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ago`;
};

export default function ExportManagerDrawer({ campId = null }) {
  const [isOpen, setIsOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeCount, setActiveCount] = useState(0);
  const [actionLoading, setActionLoading] = useState({});
  const [toast, setToast] = useState(null); // { title, filename } for auto-download toast
  const pollTimerRef = useRef(null);
  const prevJobStatusesRef = useRef({}); // jobId -> status, tracks previous poll statuses
  const autoDownloadedRef = useRef(new Set()); // prevent double auto-download

  const fetchJobs = useCallback(async () => {
    try {
      const res = await API.camp.getExportJobs(campId ? { camp_id: campId } : {});
      const list = res?.jobs || res?.data?.jobs || [];

      // --- Auto-download detection ---
      // Check if any job just transitioned from active → completed since last poll
      list.forEach((job) => {
        const prevStatus = prevJobStatusesRef.current[job.id];
        const wasActive = prevStatus === "pending" || prevStatus === "processing";
        const isNowDone = job.status === "completed";
        const notYetAutoDownloaded = !autoDownloadedRef.current.has(job.id);

        if (wasActive && isNowDone && notYetAutoDownloaded) {
          autoDownloadedRef.current.add(job.id);
          const downloadUrl = job.download_url || API.camp.getExportDownloadUrl(job.id);
          const filename = job.file_name || `${job.title || "Report"}.pdf`;
          // Trigger download silently in background with strict session deduplication
          const didDownload = triggerJobAutoDownload(job.id, downloadUrl, filename);
          if (didDownload) {
            setToast({ title: job.title || filename, filename });
            setTimeout(() => setToast(null), 5000);
          }
        }

        // Update tracked status
        prevJobStatusesRef.current[job.id] = job.status;
      });
      // --- End auto-download detection ---

      setJobs(list);
      const running = list.filter(
        (j) => j.status === "pending" || j.status === "processing"
      ).length;
      setActiveCount(running);
      return running;
    } catch (err) {
      console.warn("Failed to fetch export jobs:", err);
      return 0;
    }
  }, [campId]);

  useEffect(() => {
    fetchJobs();
    const handleJobEvent = () => { fetchJobs(); setIsOpen(true); };
    window.addEventListener("camp-export-job-started", handleJobEvent);
    window.addEventListener("camp-export-job-updated", fetchJobs);
    window.addEventListener("camp-export-job-completed", fetchJobs);
    window.addEventListener("camp-export-job-failed", fetchJobs);
    return () => {
      window.removeEventListener("camp-export-job-started", handleJobEvent);
      window.removeEventListener("camp-export-job-updated", fetchJobs);
      window.removeEventListener("camp-export-job-completed", fetchJobs);
      window.removeEventListener("camp-export-job-failed", fetchJobs);
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [fetchJobs]);

  // Fetch once when drawer opens
  useEffect(() => {
    if (isOpen) {
      fetchJobs();
    }
  }, [isOpen, fetchJobs]);

  // Only poll periodically if there are pending or processing jobs
  useEffect(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (activeCount > 0) {
      pollTimerRef.current = setInterval(() => {
        fetchJobs();
      }, 3000);
    }
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [activeCount, fetchJobs]);

  const setJobActionLoading = (jobId, state) =>
    setActionLoading((prev) => ({ ...prev, [jobId]: state }));

  const handleStopJob = async (job, e) => {
    e.stopPropagation();
    if (actionLoading[job.id]) return;
    const result = await Swal.fire({
      title: "Stop Export?",
      text: `Cancel the ongoing PDF generation for "${job.title}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Stop It",
      cancelButtonText: "Keep Running",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#475569",
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    setJobActionLoading(job.id, "stopping");
    try {
      await API.camp.cancelExportJob(job.id);
      await fetchJobs();
    } catch (err) {
      console.error("Failed to cancel export job:", err);
      Swal.fire("Error", "Could not cancel the job. Please try again.", "error");
    } finally {
      setJobActionLoading(job.id, null);
    }
  };

  const handleResumeJob = async (job, e) => {
    e.stopPropagation();
    if (actionLoading[job.id]) return;
    setJobActionLoading(job.id, "resuming");
    try {
      await API.camp.retryExportJob(job.id);
      await fetchJobs();
    } catch (err) {
      console.error("Failed to resume export job:", err);
      Swal.fire("Error", "Could not resume the export job. Please try again.", "error");
    } finally {
      setJobActionLoading(job.id, null);
    }
  };

  const handleDownload = (job, e) => {
    e.stopPropagation();
    const downloadUrl = job.download_url || API.camp.getExportDownloadUrl(job.id);
    const filename = job.file_name || `${job.title || "Report"}.pdf`;
    triggerBrowserFileDownload(downloadUrl, filename);
  };

  const handleDeleteJob = async (job, e) => {
    e.stopPropagation();
    if (actionLoading[job.id]) return;
    const isActive = job.status === "pending" || job.status === "processing";
    const result = await Swal.fire({
      title: isActive ? "Stop & Delete Export?" : "Delete Export Record?",
      html: `<div style="font-size:13px;color:#334155;">
        ${isActive
          ? `This will <strong>cancel</strong> the running job and <strong>delete</strong> the record for:<br><strong>${job.title}</strong>`
          : `This will permanently delete the record for:<br><strong>${job.title}</strong>`}
        ${job.status === "completed" ? "<br><span style='font-size:11px;color:#94a3b8;'>The generated PDF file on the server will also be removed.</span>" : ""}
      </div>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#475569",
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    setJobActionLoading(job.id, "deleting");
    try {
      await API.camp.deleteExportJob(job.id);
      setJobs((prev) => prev.filter((j) => j.id !== job.id));
    } catch (err) {
      console.error("Failed to delete export job:", err);
      Swal.fire("Error", "Could not delete the export record.", "error");
    } finally {
      setJobActionLoading(job.id, null);
    }
  };

  const completedJobsCount = jobs.filter((j) => j.status === "completed").length;
  const failedCount = jobs.filter((j) => j.status === "failed" || j.status === "cancelled").length;

  return (
    <div className="fixed bottom-3 right-3 sm:bottom-5 sm:right-5 z-[9999] font-sans flex flex-col items-end gap-3 max-w-[calc(100vw-1.5rem)]">

      {/* Auto-download Toast Notification */}
      {toast && (
        <div
          className="flex items-start gap-3 bg-slate-900 border border-emerald-500/40 text-white rounded-2xl shadow-2xl px-4 py-3 max-w-[340px] animate-bounce-in"
          style={{
            animation: "slideInRight 0.35s cubic-bezier(0.34,1.56,0.64,1) forwards",
          }}
        >
          <div className="w-8 h-8 rounded-xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0 mt-0.5">
            <FaDownload className="text-emerald-400 text-sm" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-black text-white">PDF Downloaded!</p>
            <p className="text-[10px] text-slate-400 truncate mt-0.5">{toast.title}</p>
            <p className="text-[10px] text-emerald-400 font-semibold mt-0.5">
              Saved to your Downloads folder
            </p>
          </div>
          <button
            onClick={() => setToast(null)}
            className="text-slate-500 hover:text-white transition mt-0.5 flex-shrink-0"
          >
            <FaTimes className="text-xs" />
          </button>
        </div>
      )}

      <style>{`
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(60px) scale(0.92); }
          to   { opacity: 1; transform: translateX(0) scale(1); }
        }
      `}</style>

      {!isOpen && (
        <button
          onClick={() => { fetchJobs(); setIsOpen(true); }}
          className={`relative flex h-11 w-11 items-center justify-center rounded-full shadow-xl transition-all transform hover:scale-105 backdrop-blur-md cursor-pointer ${
            activeCount > 0
              ? "bg-gradient-to-r from-cyan-600 via-sky-600 to-blue-700 text-white shadow-cyan-500/30 animate-pulse border border-cyan-400/40"
              : "bg-slate-900/90 text-white shadow-slate-900/40 border border-slate-700/80 hover:bg-slate-900"
          }`}
          title="Open PDF Export & Downloads Manager"
        >
          {activeCount > 0
            ? <FaSpinner className="animate-spin text-cyan-200 text-sm" />
            : <FaFilePdf className="text-red-400 text-base" />}
          {jobs.length > 0 && (
            <span className={`absolute -right-1 -top-1 min-w-5 h-5 px-1 flex items-center justify-center rounded-full text-[9px] font-bold border-2 border-white ${
              activeCount > 0 ? "bg-white text-cyan-700" : "bg-cyan-500 text-white"
            }`}>
              {jobs.length}
            </span>
          )}
        </button>
      )}

      {isOpen && (
        <div className="w-[calc(100vw-1.5rem)] sm:w-[440px] max-h-[calc(100vh-1.5rem)] sm:max-h-[82vh] flex flex-col bg-slate-950/95 text-slate-100 rounded-3xl shadow-2xl border border-slate-800/90 backdrop-blur-xl overflow-hidden">
          <div className="p-4 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-slate-800 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-cyan-500/20 text-cyan-400 flex items-center justify-center border border-cyan-500/30">
                <FaFilePdf className="text-sm" />
              </div>
              <div>
                <h4 className="text-xs font-black text-white tracking-wide uppercase">PDF Downloads Manager</h4>
                <p className="text-[10px] text-slate-400 font-medium">Server-side high-speed vector generation</p>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => { setLoading(true); fetchJobs().finally(() => setLoading(false)); }}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
                title="Refresh jobs"
              >
                <FaSync className={`text-xs ${loading ? "animate-spin" : ""}`} />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition"
                title="Minimize"
              >
                <FaTimes className="text-xs" />
              </button>
            </div>
          </div>

          <div className="px-4 py-2 bg-slate-900/60 border-b border-slate-800/60 flex items-center justify-between text-[11px] text-slate-300 font-medium">
            <span>
              Total: <strong className="text-white">{jobs.length}</strong> &bull; Done:{" "}
              <strong className="text-emerald-400">{completedJobsCount}</strong>
              {failedCount > 0 && <> &bull; Failed/Cancelled: <strong className="text-rose-400">{failedCount}</strong></>}
            </span>
            {activeCount > 0 && (
              <span className="flex items-center gap-1.5 text-cyan-400 font-semibold animate-pulse">
                <FaSpinner className="animate-spin text-[10px]" />
                <span>{activeCount} Generating...</span>
              </span>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain divide-y divide-slate-800/60 p-2 space-y-2">
            {jobs.length === 0 ? (
              <div className="text-center py-10 px-4 text-slate-400">
                <FaFilePdf className="mx-auto text-3xl mb-2 text-slate-600" />
                <p className="text-xs font-semibold text-slate-300">No PDF exports queued yet</p>
                <p className="text-[10px] text-slate-500 mt-1 max-w-xs mx-auto">
                  Downloads from attendance registers, ID cards, and reports will appear here.
                </p>
              </div>
            ) : (
              jobs.map((job) => {
                const isProcessing = job.status === "processing" || job.status === "pending";
                const isDone = job.status === "completed";
                const isFailed = job.status === "failed";
                const isCancelled = job.status === "cancelled";
                const isActioning = actionLoading[job.id];

                return (
                  <div
                    key={job.id}
                    className={`p-3 rounded-2xl border transition-all ${
                      isProcessing ? "bg-cyan-950/25 border-cyan-700/40"
                      : isDone ? "bg-slate-900/60 border-slate-800/80 hover:border-slate-700"
                      : isCancelled ? "bg-slate-900/40 border-slate-700/40 opacity-70"
                      : "bg-rose-950/20 border-rose-800/40"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-bold text-white truncate block">{job.title || "PDF Report"}</span>
                        <p className="text-[10px] text-slate-300 mt-0.5 truncate" title={job.current_step}>
                          {job.current_step || (isDone ? "Ready for download" : isCancelled ? "Cancelled" : isProcessing ? "Processing..." : "Failed")}
                        </p>
                        {isProcessing && job.started_at && (
                          <p className="text-[9.5px] text-cyan-400 font-medium mt-0.5 flex items-center gap-1">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-cyan-400 animate-ping" />
                            <span>Started {formatTimeAgo(job.started_at)}</span>
                          </p>
                        )}
                        {isFailed && job.error_message && (
                          <p className="text-[9.5px] text-rose-300/90 mt-0.5 line-clamp-2" title={job.error_message}>
                            {job.error_message}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0 pt-0.5">
                        {isProcessing && (
                          <span className="flex items-center gap-1 text-[11px] font-bold text-cyan-400">
                            <FaSpinner className="animate-spin text-xs" />
                            <span>{Math.round(job.progress || 0)}%</span>
                          </span>
                        )}
                        {isDone && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-400 bg-emerald-950/60 px-2 py-0.5 rounded-full border border-emerald-800/50">
                            <FaCheckCircle /> Done
                          </span>
                        )}
                        {isFailed && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-400 bg-rose-950/60 px-2 py-0.5 rounded-full border border-rose-800/50">
                            <FaExclamationTriangle /> Failed
                          </span>
                        )}
                        {isCancelled && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-400 bg-slate-800/60 px-2 py-0.5 rounded-full border border-slate-700/50">
                            <FaBan /> Cancelled
                          </span>
                        )}
                      </div>
                    </div>

                    {isProcessing && (
                      <div className="mt-2.5">
                        <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-cyan-500 via-sky-400 to-teal-400 rounded-full transition-all duration-500"
                            style={{ width: `${Math.max(5, Math.min(100, job.progress || 0))}%` }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="mt-2.5 pt-2 border-t border-slate-800/60 flex items-center justify-between text-[10px] text-slate-400">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        {job.total_pages && (
                          <span className="flex items-center gap-1" title="Pages">
                            <FaLayerGroup className="text-slate-500" /><span>{job.total_pages}p</span>
                          </span>
                        )}
                        {job.file_size_formatted && (
                          <span className="flex items-center gap-1" title="File Size">
                            <FaHdd className="text-slate-500" /><span>{job.file_size_formatted}</span>
                          </span>
                        )}
                        {job.duration_seconds && (
                          <span className="flex items-center gap-1" title="Duration">
                            <FaClock className="text-slate-500" /><span>{job.duration_seconds}s</span>
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1.5">
                        {isProcessing && (
                          <button
                            onClick={(e) => handleStopJob(job, e)}
                            disabled={!!isActioning}
                            className="px-2.5 py-1 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded-lg font-bold text-[10px] flex items-center gap-1 transition cursor-pointer"
                            title="Stop export"
                          >
                            {isActioning === "stopping"
                              ? <FaSpinner className="animate-spin text-[9px]" />
                              : <FaStop className="text-[9px]" />}
                            Stop
                          </button>
                        )}
                        {(isFailed || isCancelled) && (
                          <button
                            onClick={(e) => handleResumeJob(job, e)}
                            disabled={!!isActioning}
                            className="px-2.5 py-1 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white rounded-lg font-bold text-[10px] flex items-center gap-1 transition cursor-pointer"
                            title="Resume / Retry export"
                          >
                            {isActioning === "resuming"
                              ? <FaSpinner className="animate-spin text-[9px]" />
                              : <FaRedo className="text-[9px]" />}
                            Resume
                          </button>
                        )}
                        {isDone && (
                          <button
                            onClick={(e) => handleDownload(job, e)}
                            className="px-2.5 py-1 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg font-bold text-[10px] flex items-center gap-1 transition cursor-pointer"
                            title="Download PDF"
                          >
                            <FaDownload className="text-[9px]" /> Download
                          </button>
                        )}
                        <button
                          onClick={(e) => handleDeleteJob(job, e)}
                          disabled={!!isActioning}
                          className="p-1.5 text-slate-500 hover:text-rose-400 disabled:opacity-40 rounded transition cursor-pointer"
                          title="Delete record"
                        >
                          {isActioning === "deleting"
                            ? <FaSpinner className="animate-spin text-[10px]" />
                            : <FaTrashAlt className="text-[10px]" />}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="p-2.5 bg-slate-900/80 border-t border-slate-800/80 text-[10px] text-slate-400 text-center">
            Files stored securely on server &bull; Fast, non-blocking downloads
          </div>
        </div>
      )}
    </div>
  );
}
